import React, { useRef, useState } from 'react';
import { useStore } from '../context/StoreContext';
import Papa from 'papaparse';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUploadCloud, FiAlertTriangle, FiPlus, FiTrash2, FiTag } = FiIcons;

const Settings = () => {
  const { data, updateData } = useStore();
  const fileInputRef = useRef(null);
  const [importStatus, setImportStatus] = useState('');
  const [newCat, setNewCat] = useState({ name: '', type: 'expense' });

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          const headers = results.meta.fields;
          if (headers.includes('rentRate') || headers.includes('address')) {
            const newProps = results.data.map(row => ({ ...row, id: row.id || `imported_${Date.now()}_${Math.random()}` }));
            updateData('properties', [...data.properties, ...newProps]);
            setImportStatus(`Successfully imported ${newProps.length} properties.`);
          } else if (headers.includes('amount') || headers.includes('category')) {
            const newTx = results.data.map(row => ({ ...row, id: row.id || `imported_${Date.now()}_${Math.random()}` }));
            updateData('transactions', [...data.transactions, ...newTx]);
            setImportStatus(`Successfully imported ${newTx.length} transactions.`);
          } else {
            setImportStatus('Error: CSV format not recognized.');
          }
        } catch (err) {
          setImportStatus('Error processing file.');
        }
      }
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAddCategory = () => {
    if (!newCat.name.trim()) return;
    const key = newCat.type === 'income' ? 'incomeCategories' : 'expenseCategories';
    if (data[key].includes(newCat.name)) return;
    
    updateData(key, [...data[key], newCat.name.trim()]);
    setNewCat({ ...newCat, name: '' });
  };

  const handleRemoveCategory = (type, category) => {
    const key = type === 'income' ? 'incomeCategories' : 'expenseCategories';
    updateData(key, data[key].filter(c => c !== category));
  };

  const handleClearData = () => {
    if (window.confirm('WARNING: This will delete ALL local data. Are you sure?')) {
      localStorage.removeItem('midtermRentalsData');
      window.location.reload();
    }
  };

  return (
    <div className="space-y-8 max-w-4xl pb-10">
      <div>
        <h2 className="text-2xl font-bold text-gray-800">Data & Settings</h2>
        <p className="text-sm text-gray-500">Configure your portfolio preferences and data management.</p>
      </div>

      {/* Category Customization Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <h3 className="font-bold text-gray-800 flex items-center">
            <SafeIcon icon={FiTag} className="mr-2 text-indigo-600" /> Financial Categories
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Income Categories */}
            <div>
              <h4 className="text-xs font-bold text-green-600 uppercase tracking-widest mb-4">Income Categories</h4>
              <div className="space-y-2 mb-4">
                {data.incomeCategories.map(cat => (
                  <div key={cat} className="flex items-center justify-between p-2 bg-green-50 rounded-lg text-sm group">
                    <span className="font-medium text-green-800">{cat}</span>
                    <button 
                      onClick={() => handleRemoveCategory('income', cat)}
                      className="text-green-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                    >
                      <SafeIcon icon={FiTrash2} size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input 
                  type="text" 
                  placeholder="New income category..."
                  className="flex-1 text-sm p-2 border rounded-lg outline-none focus:ring-2 focus:ring-green-500"
                  value={newCat.type === 'income' ? newCat.name : ''}
                  onChange={e => setNewCat({ name: e.target.value, type: 'income' })}
                />
                <button onClick={handleAddCategory} className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                  <SafeIcon icon={FiPlus} />
                </button>
              </div>
            </div>

            {/* Expense Categories */}
            <div>
              <h4 className="text-xs font-bold text-red-600 uppercase tracking-widest mb-4">Expense Categories</h4>
              <div className="space-y-2 mb-4 h-[200px] overflow-y-auto pr-2 custom-scrollbar">
                {data.expenseCategories.map(cat => (
                  <div key={cat} className="flex items-center justify-between p-2 bg-red-50 rounded-lg text-sm group">
                    <span className="font-medium text-red-800">{cat}</span>
                    <button 
                      onClick={() => handleRemoveCategory('expense', cat)}
                      className="text-red-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                    >
                      <SafeIcon icon={FiTrash2} size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input 
                  type="text" 
                  placeholder="New expense category..."
                  className="flex-1 text-sm p-2 border rounded-lg outline-none focus:ring-2 focus:ring-red-500"
                  value={newCat.type === 'expense' ? newCat.name : ''}
                  onChange={e => setNewCat({ name: e.target.value, type: 'expense' })}
                />
                <button onClick={handleAddCategory} className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                  <SafeIcon icon={FiPlus} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Bulk Import (CSV)</h3>
        <p className="text-gray-500 text-sm mb-6">Upload a CSV file to bulk import Properties or Transactions.</p>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:bg-gray-50 transition cursor-pointer" onClick={() => fileInputRef.current?.click()}>
          <SafeIcon icon={FiUploadCloud} className="mx-auto h-12 w-12 text-gray-400 mb-3" />
          <p className="text-sm text-gray-600 font-medium">Click to select a CSV file</p>
          <input type="file" accept=".csv" ref={fileInputRef} onChange={handleFileUpload} className="hidden" />
        </div>
        {importStatus && <p className="mt-4 text-sm font-medium text-indigo-600">{importStatus}</p>}
      </div>

      <div className="bg-red-50 p-6 rounded-xl border border-red-100">
        <h3 className="text-lg font-medium text-red-800 mb-2 flex items-center">
          <SafeIcon icon={FiAlertTriangle} className="mr-2" /> Danger Zone
        </h3>
        <p className="text-red-600 text-sm mb-4">Clearing data cannot be undone.</p>
        <button onClick={handleClearData} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-bold">
          Factory Reset (Clear All Data)
        </button>
      </div>
    </div>
  );
};

export default Settings;